import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { logger } from '@/utils/logger';
import { captureException, addBreadcrumb } from '@/lib/sentry';
import { safeReload } from '@/utils/safeReload';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId?: string;
}

export class ErrorBoundaryEnhanced extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorId: `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorId = this.state.errorId || 'unknown';
    
    // Enhanced error logging with security considerations
    const sanitizedError = {
      message: error.message.replace(/[<>'"]/g, ''), // Basic XSS prevention
      stack: error.stack?.substring(0, 1000), // Limit stack trace length
      componentStack: errorInfo.componentStack?.substring(0, 1000),
      errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent.substring(0, 200), // Limit user agent
      url: window.location.href
    };
    
    logger.error('Error boundary caught error:', sanitizedError);

    // Send to Sentry with context
    addBreadcrumb('Error boundary triggered', 'error', {
      errorId,
      url: window.location.href,
      userAgent: navigator.userAgent.substring(0, 200)
    });
    
    captureException(error, {
      component: 'ErrorBoundaryEnhanced',
      action: 'componentDidCatch',
      errorId,
      componentStack: errorInfo.componentStack?.substring(0, 1000)
    });
    
    // Store error details in sessionStorage for debugging (limited data)
    try {
      sessionStorage.setItem(`error_${errorId}`, JSON.stringify({
        message: sanitizedError.message,
        timestamp: sanitizedError.timestamp
      }));
    } catch (e) {
      // Ignore storage errors
    }
    
    this.setState({ errorInfo });
  }

  private handleReload = () => {
    // Clear any cached data that might be causing issues
    try {
      localStorage.removeItem('lastError');
    } catch (e) {
      // Ignore storage errors
    }
    
    safeReload();
  };

  private handleGoHome = () => {
    // Clear error state and navigate to home
    try {
      sessionStorage.clear();
    } catch (e) {
      // Ignore storage errors
    }
    
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-red-900 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <CardTitle className="text-xl text-red-800 dark:text-red-200">
                Une erreur est survenue
              </CardTitle>
              <CardDescription className="text-red-600 dark:text-red-400">
                L'application a rencontré un problème inattendu.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div className="text-center text-sm text-muted-foreground">
                <p>ID d'erreur : {this.state.errorId}</p>
                <p className="mt-1">
                  {this.state.error?.message && 
                    this.state.error.message.length < 100 ? 
                    this.state.error.message : 
                    'Erreur technique détectée'
                  }
                </p>
              </div>
              
              <div className="flex flex-col gap-2">
                <Button 
                  onClick={this.handleReload}
                  className="w-full"
                  variant="default"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Recharger la page
                </Button>
                
                <Button 
                  onClick={this.handleGoHome}
                  className="w-full"
                  variant="outline"
                >
                  <Home className="mr-2 h-4 w-4" />
                  Retour à l'accueil
                </Button>
              </div>
              
              <div className="text-xs text-center text-muted-foreground">
                <p>
                  Si le problème persiste, contactez le support avec l'ID d'erreur.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}